// recurrenceHelpers.js - improved version
// Expands Plone Event recurrences from multiple field sources
// - Handles DTSTART / RRULE / RDATE / EXDATE
// - Converts basic ICS stamps (20250917T000000Z) to ISO for reliable Date()
// - Enhanced field detection for various Plone recurrence formats

const toExtendedISO = (s) => {
  if (!s || typeof s !== 'string') return s;
  const t = s.trim().replace(/"/g, '');
  // Pattern: YYYYMMDDTHHMMSSZ
  const m = t.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}Z`;
  // Pattern: YYYYMMDD (date-only)
  const m2 = t.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (m2) return `${m2[1]}-${m2[2]}-${m2[3]}T00:00:00Z`;
  return t;
};

const toDate = (v) => {
  if (!v) return null;
  const ext = typeof v === 'string' ? toExtendedISO(v) : v;
  const d = ext instanceof Date ? ext : new Date(ext);
  return isNaN(d.getTime()) ? null : d;
};

function parseICSRecurrence(input) {
  const out = { dtstart: null, rruleText: null, rdates: [], exdates: [] };
  if (!input) return out;
  
  const text = String(input).trim();
  const hasLines = /[\r\n]/.test(text) || /^DTSTART:|^RRULE:|^RDATE:|^EXDATE:/m.test(text);
  const lines = hasLines ? text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean) : [text];

  for (let raw of lines) {
    if ((raw.startsWith('"') && raw.endsWith('"')) || (raw.startsWith("'") && raw.endsWith("'"))) {
      raw = raw.slice(1, -1);
    }
    const U = raw.toUpperCase();
    if (U.startsWith('DTSTART')) {
      const val = raw.split(':').slice(1).join(':').trim();
      const dt = toDate(val);
      if (dt) out.dtstart = dt;
    } else if (U.startsWith('RRULE')) {
      const right = raw.split(':').slice(1).join(':').trim();
      out.rruleText = `RRULE:${right}`;
    } else if (U.startsWith('RDATE')) {
      const vals = raw.split(':').slice(1).join(':').trim().split(',').map((s) => s.trim()).filter(Boolean);
      vals.forEach((v) => { const d = toDate(v); if (d) out.rdates.push(d); });
    } else if (U.startsWith('EXDATE')) {
      const vals = raw.split(':').slice(1).join(':').trim().split(',').map((s) => s.trim()).filter(Boolean);
      vals.forEach((v) => { const d = toDate(v); if (d) out.exdates.push(d); });
    } else if (!out.rruleText && !hasLines) {
      const s = raw.trim();
      out.rruleText = s.startsWith('RRULE:') ? s : `RRULE:${s}`;
    }
  }
  return out;
}

// Enhanced field detection for various Plone recurrence storage formats
function extractRecurrenceData(item) {
  let recurrenceData = null;
  let occurrencesData = null;

  // Check nested recurrence object first
  if (item.recurrence) {
    if (typeof item.recurrence === 'string') {
      recurrenceData = item.recurrence;
    } else if (typeof item.recurrence === 'object') {
      recurrenceData =
        item.recurrence.rrule ||
        item.recurrence.RRULE ||
        item.recurrence.rule ||
        item.recurrence;
      occurrencesData =
        item.recurrence.occurrences ||
        item.recurrence.recurrence_occurrences ||
        null;
    }
  }

  // Check top-level fields
  recurrenceData =
    recurrenceData ||
    item.RRULE ||
    item.rrule ||
    item.recurrence_rule ||
    item.recurrenceRule ||
    item._recurrence ||
    null;

  occurrencesData =
    occurrencesData ||
    item.occurrences ||
    item.recurrence_occurrences ||
    item.recurrenceOccurrences ||
    item._occurrences ||
    null;

  return { recurrenceData, occurrencesData };
}

export async function expandRecurrences(items) {
  let rrulestr = null;
  try {
    const mod = await import('rrule');
    rrulestr = mod.rrulestr;
  } catch {
    // no rrule in bundle; we'll still honor explicit occurrences if present
  }

  const now = new Date();
  const windowStart = new Date(now.getFullYear() - 1, 0, 1);
  const windowEnd = new Date(now.getFullYear() + 3, 11, 31);
  const MAX = 800;
  const out = [];

  for (const ev of items) {
    const { recurrenceData, occurrencesData } = extractRecurrenceData(ev);

    // 1) explicit occurrences
    if (Array.isArray(occurrencesData) && occurrencesData.length) {
      occurrencesData.forEach((o, idx) => {
        const start = Array.isArray(o) ? o[0] : (o?.start ?? o);
        const end = Array.isArray(o) ? o[1] : (o?.end ?? ev.end);
        out.push({ ...ev, start, end, _seqIndex: idx + 1, _seqTotal: occurrencesData.length });
      });
      continue;
    }

    // 2) ICS recurrence (DTSTART/RRULE/RDATE/EXDATE)
    if (recurrenceData && rrulestr) {
      const parsed = parseICSRecurrence(recurrenceData);
      const baseStart = parsed.dtstart || toDate(ev.start) || now;
      const baseEnd = toDate(ev.end);
      const duration = baseStart && baseEnd ? Math.max(0, baseEnd - baseStart) : 0;

      if (parsed.rruleText) {
        try {
          const rule = rrulestr(parsed.rruleText, { dtstart: baseStart });
          let dates = [];
          try { 
            dates = rule.all((_, i) => i < MAX); 
          } catch { 
            dates = rule.between(windowStart, windowEnd, true).slice(0, MAX); 
          }

          const rdateIn = (parsed.rdates || []).filter((d) => d >= windowStart && d <= windowEnd);
          let allDates = dates.concat(rdateIn);
          const exset = new Set((parsed.exdates || []).map((d) => d.toISOString()));
          const seen = new Set();
          allDates = allDates.filter((d) => {
            const iso = d.toISOString();
            if (exset.has(iso)) return false;
            if (seen.has(iso)) return false;
            seen.add(iso);
            return true;
          }).sort((a, b) => a - b);

          if (allDates.length) {
            allDates.forEach((d, idx) => {
              out.push({
                ...ev,
                start: d.toISOString(),
                end: duration ? new Date(d.getTime() + duration).toISOString() : ev.end,
                _seqIndex: idx + 1,
                _seqTotal: allDates.length,
              });
            });
            continue;
          }
        } catch {
          // fall through to base event
        }
      }
    }

    // 3) fallback: base event (1/1)
    out.push({ ...ev, _seqIndex: 1, _seqTotal: 1 });
  }

  return out;
}